import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import axios from 'axios';
import getData from 'utils/getData';
import { wrapPageSagaWithErrorHandler } from 'core/page/sagas';
import { SOCURE_RESPONSE_STATUS } from 'config/socure';
import uiActions from 'core/ui/actions';
import groupActions from 'core/groups/actions';
import { navigate } from '@reach/router';
import { API_ROOT } from '../../config/api';
import fieldActions from './actions';
import pageActions from '../page/actions';
import dataActions from '../data/actions';
import validValidators from '../../validators';

export const runValidators = (validators, input, fields, fieldValues, locale) => {
  if (!validators || validators.length === 0 || !input) {
    return null;
  }
  for (let i = 0; i < validators.length; i += 1) {
    const currValidator = validValidators[validators[i].name];
    if (typeof currValidator === 'function') {
      const v = validators[i];
      const errorMsg = currValidator({
        configs: v?.configs || {},
        fieldValues,
        fields,
        input,
        locale,
        msgs: v?.msgs || {},
        params: v?.params || {},
      });
      if (errorMsg) {
        return errorMsg;
      }
    }
  }
  return null;
};

function* getAtomicFiToken({ payload }) {
  try {
    const { refs } = yield select();
    const { config } = payload;
    const { data } = yield axios.get(
      `${API_ROOT}/workflow/directDeposit/${refs.currentCustomerId}`,
    );
    yield put(
      fieldActions.atomicFiGetTokenSuccess({
        ...config,
        publicToken: data.message.data.publicToken,
      }),
    );
  } catch (e) {
    const { response: responsePayload } = e;
    yield put(
      fieldActions.atomicFiGetTokenFailed({
        fieldName: payload.fieldName,
        response: responsePayload,
      }),
    );
  }
}

function* atomicFiUpdateSwitchAttempts({ payload }) {
  try {
    const { page, refs } = yield select();
    const { data } = yield axios.get(
      `${API_ROOT}/workflow/directDeposit/${page.name}/${refs.currentCustomerId}/updateSwitchAttempts`,
    );
    yield put(pageActions.updatePageData(data));
  } catch (e) {}
}

function* atomicFiFailed({ payload }) {
  const { fieldName, response } = payload;
  const errorMsg = response?.data?.msg || response?.data?.result?.msgs[fieldName] || '';
  yield put(fieldActions.setError(fieldName, errorMsg));
}

function* atomicFiTransact({ payload }) {
  try {
    // eslint-disable-next-line no-undef
    yield Atomic?.transact(payload);
  } catch (e) {
    // Do nothing
  }
}

function* fetchAutoComplete({ payload }) {
  const response = yield call(payload.request);
  const { data } = response;
  yield put(fieldActions.fetchSuccess(data, payload.fieldName));
}

function* validate({ payload }) {
  const { data, fields, session } = yield select();
  const { fieldName, listGroupName, listIndex, withReRender } = payload;
  const fieldKey = listGroupName ? `${fieldName}:${listIndex}` : fieldName;
  const locale = session?.meta?.configs?.locale ?? 'en-US';
  const {
    fieldType,
    reRender: fieldReRender,
    readOnly,
    required,
    requiredErrorMsg,
    validators,
  } = fields[fieldKey];

  if (readOnly) return;

  const fieldValue = listGroupName ? data[listGroupName][listIndex][fieldName] : data[fieldName];
  const validatorError = runValidators(
    validators,
    fieldValue,
    fields,
    listGroupName ? data[listGroupName][listIndex] : data,
    locale,
  );
  const errorMsg =
    validatorError ||
    (required && validValidators.requiredValidator({ fieldType, fieldValue, requiredErrorMsg }));
  if (errorMsg) {
    yield put(fieldActions.setError(fieldKey, errorMsg));
  } else {
    yield put(fieldActions.validateSuccess(fieldKey));
    if (fieldReRender && withReRender) {
      yield put(fieldActions.reRender(fieldKey));
    }
  }
}

function* reRender({ payload }) {
  const { fields, groups, page } = yield select();
  const dataState = yield select(getData);
  const currentGroup = Object.keys(groups).find((key) =>
    groups[key]?.fields?.includes(payload.fieldName),
  );
  const forceUpdate = payload.fieldName && payload.fieldName.includes('AddressSameAs');
  if (fields[payload.fieldName].error) {
    return;
  }

  // Clear validation errors of all fields in the offers groups
  if (groups[currentGroup]?.staticGroupType?.toLowerCase().includes('offers_')) {
    yield all(groups[currentGroup]?.fields.map((key) => put(fieldActions.validateSuccess(key))));
  }

  try {
    const response = yield call(payload.request(page.name, dataState));
    const { data } = response;

    yield put(fieldActions.reRenderSuccess(data, forceUpdate));
    if (fields[payload.fieldName].fieldType === 'VERIFICATION_TYPE') {
      yield put(
        groupActions.handleVerificationOptions(
          currentGroup,
          fields[payload.fieldName].mobilePhoneField,
        ),
      );
    }
  } catch (e) {
    const {
      response: { data, status },
    } = e;
    if (status === 400) {
      yield put(fieldActions.reRenderFailed(data));
    } else {
      yield put(pageActions.goToPageError(data, status));
    }
  }
}

function* googlePlacesAutocomplete({ payload }) {
  try {
    const { data, fields } = yield select();
    const listGroupName = Object.keys(data).find((name) => name.includes('___list'));

    yield all(
      Object.keys(payload.data).map((key) => {
        const clientId = `${payload.clientIdPrefix}${key}`;
        const field = fields[clientId] || fields[`${clientId}:${payload.clientIdIndex}`];

        if (field && field.fieldType !== 'OUTPUT' && !field.readOnly) {
          // List group
          if (payload.clientIdIndex && listGroupName) {
            return put(
              dataActions.changeListGroupData(
                listGroupName,
                Number(payload.clientIdIndex),
                clientId,
                payload.data[key],
              ),
            );
          }

          // Normal data
          return put(dataActions.changeData(clientId, payload.data[key]));
        }

        return null;
      }),
    );

    yield all(
      Object.keys(payload.data).map((key) => {
        const clientId = `${payload.clientIdPrefix}${key}`;
        const field = fields[clientId] || fields[`${clientId}:${payload.clientIdIndex}`];

        if (field && field.fieldType !== 'OUTPUT' && !field.readOnly) {
          if (payload.clientIdIndex && listGroupName) {
            return put(
              fieldActions.validate(clientId, true, listGroupName, Number(payload.clientIdIndex)),
            );
          }
          return put(fieldActions.validate(clientId));
        }

        return null;
      }),
    );

    yield put(fieldActions.googlePlacesAutocompleteSuccess());
  } catch (e) {
    yield put(fieldActions.googlePlacesAutocompleteFailed(e));
  }
}

function* socureAutocomplete({ payload }) {
  const { data: payloadData = undefined } = payload;
  if (payloadData)
    try {
      const appRequestType = yield select(({ page }) => page.name);
      const response = yield axios.post(
        `${API_ROOT}/pages/${appRequestType}/socure/prefill`,
        payloadData,
      );
      const { data } = response;
      if (!payloadData.mobileNumber && payloadData.url?.length > 0) {
        window.location = payloadData.url;
      } else if (data?.meta?.nextPage) {
        yield put(pageActions.nextSuccess(data));
      }
      yield put(pageActions.goToPageSuccess(data));
    } catch (e) {
      const {
        response: { data, status },
      } = e;
      if (status === 400) {
        yield put(fieldActions.reRenderFailed(data, true));
      } else {
        yield put(pageActions.goToPageError(data, status));
      }
    }
}

function* socureRetrieveInformation({ payload }) {
  const { referenceId = undefined } = payload;
  if (referenceId)
    try {
      const appRequestType = yield select(({ page }) => page.name);
      const { data } = yield axios.get(`${API_ROOT}/pages/${appRequestType}/socure/prefill`, {
        params: { referenceId },
      });
      yield put(pageActions.goToPageSuccess(data));
      const { meta } = data;
      if (
        meta.config?.socureStatus !== SOCURE_RESPONSE_STATUS.VERIFICATION_COMPLETE &&
        !data.data.appRequest_primary_iddoc1_identityDocumentType
      ) {
        yield put(uiActions.setModalOpen(true));
      }
    } catch (e) {
      const {
        response: { data, status },
      } = e;
      if (status === 400) {
        yield put(fieldActions.reRenderFailed(data));
      } else {
        yield put(pageActions.goToPageError(data, status));
      }
    }
}

function* revalidateAmountSelectors({ payload }) {
  if (payload.data?.meta?.fields) {
    const amountSelectorFields = Object.values(payload.data.meta.fields).filter(
      (field) => field.fieldType === 'AMOUNT_SELECTOR',
    );
    yield all(amountSelectorFields.map((field) => put(fieldActions.validate(field.clientId))));
  }
}

export default [
  takeEvery(fieldActions.ATOMICFI_GET_TOKEN, wrapPageSagaWithErrorHandler(getAtomicFiToken)),
  takeEvery(
    fieldActions.ATOMICFI_GET_TOKEN_SUCCESS,
    wrapPageSagaWithErrorHandler(atomicFiTransact),
  ),
  takeEvery(fieldActions.ATOMICFI_GET_TOKEN_FAILED, atomicFiFailed),
  takeEvery(
    fieldActions.ATOMICFI_UPDATE_SWITCH_ATTEMPTS,
    wrapPageSagaWithErrorHandler(atomicFiUpdateSwitchAttempts),
  ),
  takeEvery(fieldActions.FETCH_AUTOCOMPLETE, wrapPageSagaWithErrorHandler(fetchAutoComplete)),
  takeEvery(fieldActions.RERENDER, reRender),
  takeEvery(fieldActions.RERENDER_SUCCESS, revalidateAmountSelectors),
  takeEvery(fieldActions.VALIDATE, wrapPageSagaWithErrorHandler(validate)),
  takeEvery(fieldActions.GOOGLE_PLACES_AUTOCOMPLETE, googlePlacesAutocomplete),
  takeEvery(fieldActions.SOCURE_AUTOCOMPLETE, socureAutocomplete),
  takeEvery(fieldActions.SOCURE_RETRIEVE_INFORMATION, socureRetrieveInformation),
];
